import AsyncStorage from "@react-native-async-storage/async-storage";
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { getMyAccounts, accountCanBeSelected, chooseActiveAccount } from "../lib/accounts";
import { activeAccountStorageKey } from "../lib/accountSelection";
import { useStartup } from "./useStartup";
import type { AccountSummary } from "../types/account";

export type AccountsResolutionState = "loading" | "resolved";

export type ActiveAccountState = {
  firebaseUid: string | null;
  accounts: AccountSummary[];
  personalAccount: AccountSummary | null;
  institutionAccounts: AccountSummary[];
  activeAccount: AccountSummary | null;
  activeAccountId: string | null;
  loading: boolean;
  accountsResolved: boolean;
  error: unknown | null;
  setActiveAccount: (accountId: string) => Promise<boolean>;
  refresh: () => Promise<void>;
};

const ActiveAccountContext = createContext<ActiveAccountState | null>(null);

export function ActiveAccountProvider({ children }: PropsWithChildren) {
  const { user, status } = useStartup();
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null);
  const [accountsResolvedForUid, setAccountsResolvedForUid] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [accountsState, setAccountsState] = useState<AccountsResolutionState>("resolved");
  const [error, setError] = useState<unknown | null>(null);
  const firebaseUid = user?.uid ?? null;
  const firebaseUidRef = useRef<string | null>(firebaseUid);
  const accountsRef = useRef<AccountSummary[]>([]);
  const accountsStateRef = useRef<AccountsResolutionState>("resolved");
  const loadPromiseRef = useRef<Promise<AccountSummary[]> | null>(null);
  const generationRef = useRef(0);
  const selectionWaitersRef = useRef(new Map<string, Set<(selected: boolean) => void>>());

  firebaseUidRef.current = firebaseUid;

  const resolveSelectionWaiters = useCallback((selected: boolean) => {
    selectionWaitersRef.current.forEach((waiters) => waiters.forEach((resolve) => resolve(selected)));
    selectionWaitersRef.current.clear();
  }, []);

  const loadAccounts = useCallback(async (uid: string, generation: number) => {
    try {
      const [resolvedAccounts, persistedAccountId] = await Promise.all([
        getMyAccounts(),
        AsyncStorage.getItem(activeAccountStorageKey(uid)).catch(() => null),
      ]);
      if (generation !== generationRef.current || firebaseUidRef.current !== uid) return [];

      const nextAccounts = resolvedAccounts.filter((account) => account.status === "active");
      const personalId = nextAccounts.find((account) => account.isPersonal)?.accountId ?? uid;
      const nextActive = chooseActiveAccount(nextAccounts, persistedAccountId, personalId);
      accountsRef.current = nextAccounts;
      accountsStateRef.current = "resolved";
      setAccounts(nextAccounts);
      setAccountsResolvedForUid(uid);
      setActiveAccountId(nextActive?.accountId ?? null);
      setError(null);
      setLoading(false);
      setAccountsState("resolved");
      if (nextActive) {
        void AsyncStorage.setItem(activeAccountStorageKey(uid), nextActive.accountId).catch(() => {});
      }
      return nextAccounts;
    } catch (nextError) {
      if (generation !== generationRef.current || firebaseUidRef.current !== uid) return [];
      accountsRef.current = [];
      accountsStateRef.current = "resolved";
      setAccounts([]);
      setAccountsResolvedForUid(uid);
      setActiveAccountId(null);
      setError(nextError);
      setLoading(false);
      setAccountsState("resolved");
      return [];
    }
  }, []);

  const resetAndLoad = useCallback((uid: string) => {
    const generation = ++generationRef.current;
    resolveSelectionWaiters(false);
    accountsRef.current = [];
    accountsStateRef.current = "loading";
    setAccountsResolvedForUid(null);
    setAccounts([]);
    setActiveAccountId(null);
    setError(null);
    setLoading(true);
    setAccountsState("loading");
    const promise = loadAccounts(uid, generation);
    loadPromiseRef.current = promise;
    void promise.finally(() => {
      if (loadPromiseRef.current === promise) loadPromiseRef.current = null;
    });
    return promise;
  }, [loadAccounts, resolveSelectionWaiters]);

  useEffect(() => {
    const uid = firebaseUid;
    const eligible = !!uid && user?.emailVerified === true && status === "ready";
    if (!eligible || !uid) {
      generationRef.current += 1;
      resolveSelectionWaiters(false);
      accountsRef.current = [];
      accountsStateRef.current = "resolved";
      setAccountsResolvedForUid(null);
      setAccounts([]);
      setActiveAccountId(null);
      setError(null);
      setLoading(false);
      setAccountsState("resolved");
      loadPromiseRef.current = Promise.resolve([]);
      return;
    }

    if (accountsStateRef.current === "loading" && loadPromiseRef.current && firebaseUidRef.current === uid) return;
    resetAndLoad(uid);
  }, [firebaseUid, resetAndLoad, resolveSelectionWaiters, status, user?.emailVerified]);

  useEffect(() => {
    if (!activeAccountId) return;
    const waiters = selectionWaitersRef.current.get(activeAccountId);
    if (!waiters) return;
    waiters.forEach((resolve) => resolve(true));
    selectionWaitersRef.current.delete(activeAccountId);
  }, [activeAccountId]);

  const personalAccount = useMemo(
    () => accounts.find((account) => account.isPersonal || account.accountId === firebaseUid) ?? null,
    [accounts, firebaseUid]
  );
  const institutionAccounts = useMemo(
    () => accounts.filter((account) => account.accountType === "institution" && !account.isPersonal),
    [accounts]
  );
  const activeAccount = useMemo(
    () => accounts.find((account) => account.accountId === activeAccountId) ?? null,
    [accounts, activeAccountId]
  );

  const setActiveAccount = useCallback(async (accountId: string) => {
    const uid = firebaseUidRef.current;
    if (!uid) return false;

    let availableAccounts = accountsRef.current;
    if (accountsStateRef.current === "loading"
      || (!availableAccounts.length && status === "ready" && user?.emailVerified === true)) {
      const pending = loadPromiseRef.current || resetAndLoad(uid);
      if (pending) availableAccounts = await pending.catch(() => []);
    }
    if (firebaseUidRef.current !== uid || accountsStateRef.current !== "resolved") return false;

    const candidate = availableAccounts.find((account) => account.accountId === accountId) ?? null;
    if (!accountCanBeSelected(availableAccounts, accountId) || !candidate) return false;

    const activeSelection = activeAccountId === candidate.accountId
      ? Promise.resolve(true)
      : new Promise<boolean>((resolve) => {
        const waiters = selectionWaitersRef.current.get(candidate.accountId) ?? new Set<(selected: boolean) => void>();
        waiters.add(resolve);
        selectionWaitersRef.current.set(candidate.accountId, waiters);
      });

    setActiveAccountId(candidate.accountId);
    await AsyncStorage.setItem(activeAccountStorageKey(uid), candidate.accountId).catch(() => {});
    return (await activeSelection) && firebaseUidRef.current === uid;
  }, [activeAccountId, resetAndLoad, status, user?.emailVerified]);

  const refresh = useCallback(async () => {
    const uid = firebaseUidRef.current;
    if (!uid || user?.emailVerified !== true || status !== "ready") return;
    await resetAndLoad(uid);
  }, [resetAndLoad, status, user?.emailVerified]);

  const value = useMemo<ActiveAccountState>(
    () => ({
      firebaseUid,
      accounts,
      personalAccount,
      institutionAccounts,
      activeAccount,
      activeAccountId,
      loading,
      accountsResolved: accountsState === "resolved" && (!firebaseUid || accountsResolvedForUid === firebaseUid),
      error,
      setActiveAccount,
      refresh,
    }),
    [
      accounts,
      accountsState,
      accountsResolvedForUid,
      activeAccount,
      activeAccountId,
      error,
      firebaseUid,
      institutionAccounts,
      loading,
      personalAccount,
      refresh,
      setActiveAccount,
    ]
  );

  return <ActiveAccountContext.Provider value={value}>{children}</ActiveAccountContext.Provider>;
}

export function useActiveAccount() {
  const value = useContext(ActiveAccountContext);
  if (!value) {
    throw new Error("useActiveAccount doit être utilisé dans ActiveAccountProvider.");
  }
  return value;
}
